import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SkeletonRows } from '../ui/Skeleton';
import { PageHeader } from '../ui/PageHeader';
import { ErrorBanner } from '../ui/ErrorBanner';
import { supabase } from '../lib/supabase';
import { fetchAllRows } from '../lib/fetchAll';
import { timeAgo } from '../lib/timeAgo';
import { useToast } from '../lib/useToast';
import { useEscToClose } from '../lib/useEscToClose';
import { ExportButton } from '../components/ExportButton';

const ATTACHMENT_BUCKET = 'feedback-attachments';

type FeedbackFilter = 'all' | 'unread' | 'pinned';

type FeedbackRow = {
  id: string;
  message: string | null;
  subject?: string | null;
  created_at: string;
  status: string | null;
  email?: string | null;
  contact_email?: string | null;
  pinned?: boolean | null;
  attachment_path?: string | null;
  profiles: { username: string | null } | { username: string | null }[] | null;
};

type FeedbackFieldSupport = { email: boolean; pinned: boolean; attachment: boolean };

// Additive: prefer a select that includes `subject`. If the column is absent in
// prod the request throws and we transparently fall back to the original
// subject-less chain below, so the page never breaks on a missing column.
const FEEDBACK_SELECT_FULL_SUBJECT =
  'id, message, subject, created_at, status, email, contact_email, pinned, attachment_path, profiles(username)';

const FEEDBACK_SELECT_FULL =
  'id, message, created_at, status, email, contact_email, pinned, attachment_path, profiles(username)';

function usernameFromRow(r: FeedbackRow): string {
  const p = r.profiles;
  if (!p) return '—';
  const row = Array.isArray(p) ? p[0] : p;
  return row?.username?.trim() || '—';
}

function emailFromRow(r: FeedbackRow): string | null {
  const e = r.email?.trim() || r.contact_email?.trim();
  return e || null;
}

function hasAttachment(r: FeedbackRow): boolean {
  return Boolean(r.attachment_path?.trim());
}

function sortFeedbackRows(rows: FeedbackRow[]): FeedbackRow[] {
  return [...rows].sort((a, b) => {
    const pa = Boolean(a.pinned);
    const pb = Boolean(b.pinned);
    if (pa !== pb) return pa ? -1 : 1;
    return b.created_at.localeCompare(a.created_at);
  });
}

function truncate(s: string, n: number): string {
  const t = s.trim();
  if (t.length <= n) return t;
  return `${t.slice(0, n)}…`;
}

function subjectFromRow(r: FeedbackRow): string | null {
  const s = r.subject?.trim();
  return s || null;
}

// Row / modal title: the subject when present, otherwise a snippet of the message.
function titleFromRow(r: FeedbackRow, n: number): string {
  const s = subjectFromRow(r);
  if (s) return truncate(s, n);
  return truncate(r.message ?? '', n) || '—';
}

async function signedAttachmentUrl(path: string): Promise<string | null> {
  // Private bucket — admins read via a signed URL (feedback_attachments_select_admin
  // storage policy). Signed fresh on open so the full-size view never hits an
  // expired token.
  const { data, error } = await supabase.storage.from(ATTACHMENT_BUCKET).createSignedUrl(path, 3600);
  if (error || !data?.signedUrl) return null;
  return data.signedUrl;
}

export function FeedbackScreen() {
  const [feedback, setFeedback] = useState<FeedbackRow[]>([]);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [feedbackFieldSupport, setFeedbackFieldSupport] = useState<FeedbackFieldSupport>({
    email: false,
    pinned: false,
    attachment: false,
  });
  const [feedbackFilter, setFeedbackFilter] = useState<FeedbackFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState<FeedbackRow | null>(null);
  const [feedbackBusyId, setFeedbackBusyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [thumbUrls, setThumbUrls] = useState<Record<string, string>>({});
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [selectedImageLoading, setSelectedImageLoading] = useState(false);

  const loadFeedback = useCallback(async () => {
    setLoading(true);
    setFeedbackError(null);
    try {
      let rows: FeedbackRow[];
      let support: FeedbackFieldSupport = { email: true, pinned: true, attachment: true };
      try {
        rows = await fetchAllRows<FeedbackRow>((from, to) =>
          supabase
            .from('user_messages')
            .select(FEEDBACK_SELECT_FULL_SUBJECT)
            .order('pinned', { ascending: false })
            .order('created_at', { ascending: false })
            .range(from, to),
        );
      } catch {
       try {
        rows = await fetchAllRows<FeedbackRow>((from, to) =>
          supabase
            .from('user_messages')
            .select(FEEDBACK_SELECT_FULL)
            .order('pinned', { ascending: false })
            .order('created_at', { ascending: false })
            .range(from, to),
        );
       } catch {
        try {
          rows = await fetchAllRows<FeedbackRow>((from, to) =>
            supabase
              .from('user_messages')
              .select('id, message, created_at, status, email, contact_email, pinned, profiles(username)')
              .order('created_at', { ascending: false })
              .range(from, to),
          );
          support = { email: true, pinned: true, attachment: false };
        } catch {
          try {
            rows = await fetchAllRows<FeedbackRow>((from, to) =>
              supabase
                .from('user_messages')
                .select('id, message, created_at, status, email, contact_email, profiles(username)')
                .order('created_at', { ascending: false })
                .range(from, to),
            );
            support = { email: true, pinned: false, attachment: false };
          } catch {
            rows = await fetchAllRows<FeedbackRow>((from, to) =>
              supabase
                .from('user_messages')
                .select('id, message, created_at, status, profiles(username)')
                .order('created_at', { ascending: false })
                .range(from, to),
            );
            support = { email: false, pinned: false, attachment: false };
          }
        }
       }
      }
      setFeedbackFieldSupport(support);
      setFeedback(sortFeedbackRows(rows));

      if (support.attachment) {
        const withPaths = rows.filter(hasAttachment);
        const entries = await Promise.all(
          withPaths.map(async (r) => {
            const url = await signedAttachmentUrl(r.attachment_path!.trim());
            return url ? ([r.id, url] as const) : null;
          }),
        );
        const next: Record<string, string> = {};
        for (const e of entries) {
          if (e) next[e[0]] = e[1];
        }
        setThumbUrls(next);
      } else {
        setThumbUrls({});
      }
    } catch (e: unknown) {
      setFeedbackError(e instanceof Error ? e.message : 'Failed to load feedback');
      setFeedback([]);
      setThumbUrls({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadFeedback();
  }, [loadFeedback]);

  // Derive stable scalars so the signing effect re-fires only when the open
  // message (or its attachment) actually changes — not when markRead/togglePin
  // produce a new `selected` object identity (which made the image flicker).
  const selectedMessageId = selected?.id ?? null;
  const selectedAttachmentPath =
    selected && feedbackFieldSupport.attachment && hasAttachment(selected)
      ? selected.attachment_path!.trim()
      : null;
  useEffect(() => {
    if (!selectedAttachmentPath) {
      setSelectedImageUrl(null);
      setSelectedImageLoading(false);
      return;
    }
    // Always sign fresh on open — a cached list-thumbnail URL may have expired by
    // the time the admin opens the message, which made the full-size view 404.
    let cancelled = false;
    setSelectedImageUrl(null);
    setSelectedImageLoading(true);
    void signedAttachmentUrl(selectedAttachmentPath).then((url) => {
      if (cancelled) return;
      setSelectedImageUrl(url);
      setSelectedImageLoading(false);
      if (url && selectedMessageId) setThumbUrls((prev) => ({ ...prev, [selectedMessageId]: url }));
    });
    return () => {
      cancelled = true;
    };
  }, [selectedAttachmentPath, selectedMessageId]);

  useEscToClose(Boolean(selected), () => setSelected(null));

  const isUnread = (r: FeedbackRow) => r.status === 'unread' || r.status == null;

  const filteredFeedback = useMemo(() => {
    let rows = feedback;
    if (feedbackFilter === 'unread') rows = rows.filter(isUnread);
    else if (feedbackFilter === 'pinned') rows = rows.filter((r) => Boolean(r.pinned));
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => {
        const haystack = [
          r.message ?? '',
          r.subject ?? '',
          usernameFromRow(r),
          emailFromRow(r) ?? '',
        ]
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
    }
    return rows;
  }, [feedback, feedbackFilter, searchQuery]);

  const { showError, toastNode } = useToast();

  function patchFeedbackRow(id: string, patch: Partial<FeedbackRow>) {
    setFeedback((prev) => sortFeedbackRows(prev.map((x) => (x.id === id ? { ...x, ...patch } : x))));
    setSelected((cur) => (cur?.id === id ? { ...cur, ...patch } : cur));
  }

  async function updateFeedbackRow(id: string, patch: Record<string, unknown>, localPatch: Partial<FeedbackRow>) {
    setFeedbackBusyId(id);
    try {
      const { error } = await supabase.from('user_messages').update(patch).eq('id', id);
      if (error) throw error;
      patchFeedbackRow(id, localPatch);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setFeedbackBusyId(null);
    }
  }

  async function markRead(m: FeedbackRow) {
    await updateFeedbackRow(m.id, { status: 'read' }, { status: 'read' });
  }

  async function markUnread(m: FeedbackRow) {
    await updateFeedbackRow(m.id, { status: 'unread' }, { status: 'unread' });
  }

  async function togglePin(m: FeedbackRow) {
    if (!feedbackFieldSupport.pinned) return;
    const next = !m.pinned;
    await updateFeedbackRow(m.id, { pinned: next }, { pinned: next });
  }

  return (
    <div className="admin-page max-w-4xl mx-auto">
      <PageHeader title="Feedback" subtitle="Help & support messages from users in the main app" />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div
          className="inline-flex rounded-lg border border-border bg-surface p-0.5"
          role="group"
          aria-label="Filter feedback"
        >
          {(['all', 'unread', 'pinned'] as const).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setFeedbackFilter(key)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                feedbackFilter === key ? 'bg-accent/25 text-white' : 'text-muted hover:text-white'
              }`}
            >
              {key}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search message, subject, user…"
            className="w-56 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs text-white placeholder:text-mutedDim focus:border-accent/60 focus:outline-none"
          />
          <ExportButton
            rows={filteredFeedback}
            filename="feedback"
            columns={[
              { header: 'username', get: (r: FeedbackRow) => usernameFromRow(r) },
              { header: 'email', get: (r: FeedbackRow) => emailFromRow(r) ?? '' },
              { header: 'subject', get: (r: FeedbackRow) => subjectFromRow(r) ?? '' },
              { header: 'message', get: (r: FeedbackRow) => r.message ?? '' },
              { header: 'status', get: (r: FeedbackRow) => (isUnread(r) ? 'unread' : 'read') },
              { header: 'created_at', get: (r: FeedbackRow) => r.created_at },
            ]}
          />
        </div>
      </div>

      {feedbackError ? (
        <ErrorBanner className="mb-4">{feedbackError}</ErrorBanner>
      ) : loading ? (
        <SkeletonRows rows={8} className="py-4" />
      ) : (
        <ul className="rounded-xl border border-border bg-surface overflow-hidden">
          {filteredFeedback.length === 0 ? (
            <li className="px-5 py-10 text-center text-muted text-sm">
              {feedback.length === 0 ? 'No messages' : 'No messages match this filter'}
            </li>
          ) : (
            filteredFeedback.map((r, i) => {
              const contactEmail = emailFromRow(r);
              const busy = feedbackBusyId === r.id;
              const thumb = thumbUrls[r.id];
              const showAttachment = feedbackFieldSupport.attachment && hasAttachment(r);
              return (
                <li
                  key={r.id}
                  className={`border-b border-border/50 last:border-b-0 ${
                    r.pinned ? 'bg-accent/[0.06]' : i % 2 === 0 ? 'bg-surface' : 'bg-rowAlt'
                  }`}
                >
                  <div className="flex items-start gap-3 px-5 py-4">
                    <button
                      type="button"
                      onClick={() => setSelected(r)}
                      className="min-w-0 flex-1 text-left flex items-start gap-4 hover:opacity-95 transition-opacity"
                    >
                      <span
                        className={`mt-2 h-2 w-2 rounded-full shrink-0 ${isUnread(r) ? 'bg-accent' : 'bg-border'}`}
                        aria-hidden
                      />
                      {showAttachment && thumb ? (
                        <img
                          src={thumb}
                          alt=""
                          className="h-14 w-14 shrink-0 rounded-lg border border-border object-cover bg-canvas"
                        />
                      ) : showAttachment ? (
                        <div className="h-14 w-14 shrink-0 rounded-lg border border-border bg-canvas flex items-center justify-center text-[10px] text-mutedDim uppercase tracking-wide">
                          Photo
                        </div>
                      ) : null}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2.5">
                          <span className="admin-label-primary">{usernameFromRow(r)}</span>
                          {contactEmail ? (
                            <span className="text-xs text-violet-300/90 truncate max-w-[220px]">{contactEmail}</span>
                          ) : null}
                          <span className="text-mutedDim text-xs">{timeAgo(r.created_at)}</span>
                          {showAttachment ? (
                            <span className="text-[10px] uppercase tracking-wider text-mutedDim">Screenshot</span>
                          ) : null}
                          {r.pinned ? (
                            <span className="text-[10px] uppercase tracking-wider text-accent">Pinned</span>
                          ) : null}
                          <span
                            className={`text-[10px] uppercase tracking-wider ${
                              isUnread(r) ? 'text-accent' : 'text-mutedDim'
                            }`}
                          >
                            {isUnread(r) ? 'Unread' : 'Read'}
                          </span>
                        </div>
                        {subjectFromRow(r) ? (
                          <p className="text-sm font-medium text-white/90 mt-1.5 truncate">
                            {truncate(subjectFromRow(r) as string, 100)}
                          </p>
                        ) : null}
                        <p className="text-sm text-muted mt-1.5 leading-relaxed truncate">
                          {truncate(r.message ?? '', 120)}
                        </p>
                      </div>
                    </button>
                    <div className="flex shrink-0 flex-col gap-1.5 sm:flex-row sm:items-center">
                      {feedbackFieldSupport.pinned ? (
                        <button
                          type="button"
                          disabled={busy}
                          title={r.pinned ? 'Unpin' : 'Pin'}
                          onClick={() => void togglePin(r)}
                          className={`rounded-md border px-2 py-1 text-[10px] font-semibold uppercase tracking-wide disabled:opacity-50 ${
                            r.pinned
                              ? 'border-accent/50 bg-accent/20 text-white'
                              : 'border-border text-muted hover:text-white'
                          }`}
                        >
                          {r.pinned ? 'Unpin' : 'Pin'}
                        </button>
                      ) : null}
                      {isUnread(r) ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void markRead(r)}
                          className="rounded-md border border-border px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted hover:text-white disabled:opacity-50"
                        >
                          Read
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void markUnread(r)}
                          className="rounded-md border border-border px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted hover:text-white disabled:opacity-50"
                        >
                          Unread
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })
          )}
        </ul>
      )}

      {selected ? (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/75 p-4"
          role="presentation"
          onClick={() => setSelected(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl border border-border bg-surface p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="font-display text-lg font-semibold text-white tracking-tight">
                  {titleFromRow(selected, 80)}
                </h3>
                <p className="text-xs text-mutedDim mt-1.5">
                  {usernameFromRow(selected)} · {timeAgo(selected.created_at)}
                </p>
                {emailFromRow(selected) ? (
                  <p className="text-sm text-violet-300/95 mt-2 break-all">{emailFromRow(selected)}</p>
                ) : (
                  <p className="text-xs text-mutedDim mt-2">No contact email on this message</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="text-muted hover:text-white text-sm px-2 py-1 -mr-1 rounded-md hover:bg-white/5"
              >
                Close
              </button>
            </div>
            <p className="text-sm text-white/95 whitespace-pre-wrap leading-relaxed mb-4">{selected.message ?? '—'}</p>
            {feedbackFieldSupport.attachment && hasAttachment(selected) ? (
              <div className="mb-6">
                <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim mb-2">Screenshot</p>
                {selectedImageLoading ? (
                  <p className="text-sm text-muted">Loading image…</p>
                ) : selectedImageUrl ? (
                  <a href={selectedImageUrl} target="_blank" rel="noopener noreferrer" className="block">
                    <img
                      src={selectedImageUrl}
                      alt="User feedback attachment"
                      className="w-full max-h-[420px] rounded-lg border border-border object-contain bg-canvas"
                    />
                    <p className="text-xs text-mutedDim mt-2">Click to open full size</p>
                  </a>
                ) : (
                  <p className="text-sm text-muted">Could not load attachment</p>
                )}
              </div>
            ) : null}
            <div className="flex flex-wrap justify-end gap-2">
              {feedbackFieldSupport.pinned ? (
                <button
                  type="button"
                  disabled={feedbackBusyId === selected.id}
                  onClick={() => void togglePin(selected)}
                  className="admin-btn admin-btn-secondary disabled:opacity-50"
                >
                  {selected.pinned ? 'Unpin' : 'Pin'}
                </button>
              ) : null}
              {isUnread(selected) ? (
                <button
                  type="button"
                  disabled={feedbackBusyId === selected.id}
                  onClick={() => void markRead(selected)}
                  className="admin-btn admin-btn-primary"
                >
                  Mark as read
                </button>
              ) : (
                <button
                  type="button"
                  disabled={feedbackBusyId === selected.id}
                  onClick={() => void markUnread(selected)}
                  className="admin-btn admin-btn-secondary disabled:opacity-50"
                >
                  Mark as unread
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {toastNode}
    </div>
  );
}
